import styles from '../PaymentModalTable.module.css';
import WrapperTable from 'ui/WrapperTable/WrapperTable';
import { CellContext, ColumnDef } from '@tanstack/react-table';
import { OptionsContext } from 'contexts/OptionsContext/OptionsContext';
import useNonNullContext from 'Hooks/useNonNullContext';
import ReferenceCell from '../TableCells/ReferenceCell/ReferenceCell';
import StatusCell from '../TableCells/StatusCell/StatusCell';
import { RequestStatus } from '../helpers';
import { OutstandingPaymentsTableRow } from '../../OutstandingPayments/helpers';
import AccountCell from '../../TableCells/AccountCell/AccountCell';
import CostLabel from 'ui/DataDisplay/Money/CostLabel/CostLabel';
import { microwave } from '@round/api';
import PlatformCell from '../../TableCells/PlatformCell/PlatformCell';

type TableRow = OutstandingPaymentsTableRow & {
    paymentRequestStatus: RequestStatus | undefined;
};

type Props = {
    data: TableRow[];
    updatePayment: typeof microwave.patchPaymentRequest;
    arePaymentsLoading: boolean;
    arePaymentsComplete: boolean;
};

type TableCellContext<K extends keyof TableRow> = CellContext<TableRow, TableRow[K]>;

const VenmoPaymentModalTable = ({ data, updatePayment, arePaymentsLoading, arePaymentsComplete }: Props) => {
    const { currencies } = useNonNullContext(OptionsContext);

    const columns: ColumnDef<TableRow, any>[] = [
        {
            header: 'Account',
            accessorKey: 'influencer',
            cell: ({ row: { original } }: TableCellContext<'influencer'>) => (
                <AccountCell
                    influencer={original.influencer || null}
                    influencerAccount={original.influencerAccount || null}
                    imageUrl={original.userImageUrl}
                    platform={original.platform}
                />
            ),
        },
        {
            header: 'Platform',
            accessorKey: 'platform',
            cell: ({ getValue }: TableCellContext<'platform'>) => <PlatformCell platform={getValue()} />,
        },
        {
            header: 'Cost',
            accessorKey: 'cost',
            cell: ({ getValue, row: { original } }: TableCellContext<'cost'>) => {
                const currency = currencies.find((currency) => currency.id === original.currency_id);
                return <CostLabel cost={getValue()} currencySymbol={currency?.symbol ?? ''} />;
            },
        },
        {
            header: 'Venmo Email Address',
            accessorKey: 'venmo_email_address',
            cell: ({ getValue }: TableCellContext<'venmo_email_address'>) => <span>{getValue()}</span>,
        },
        {
            header: 'Reference',
            accessorKey: 'payment_reference',
            cell: ({ getValue, row: { original } }: TableCellContext<'payment_reference'>) => (
                <ReferenceCell
                    initialReference={getValue()}
                    onChange={(value) => updatePayment(original.id, { payment_reference: value })}
                    readonly={arePaymentsLoading || arePaymentsComplete}
                />
            ),
        },
        {
            header: 'Status',
            accessorKey: 'paymentRequestStatus',
            cell: ({ getValue }: TableCellContext<'paymentRequestStatus'>) => (
                <StatusCell status={getValue()} isRequestLoading={arePaymentsLoading} />
            ),
        },
    ];

    return <WrapperTable data={data} columns={columns} className={styles.table} />;
};

export default VenmoPaymentModalTable;
